import * as React from 'react'
import type { RouteObject } from 'react-router-dom'
import { Navigate, Route, Routes } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  JobCatalogRootPageDocument,
  JobRoleHeaderDocument,
  NodePageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import type { MetaContext } from 'modules/core/lib/navigation/routes'
import { buildNavigation } from 'modules/core/lib/react-router'

import { JobRoleHeader } from 'modules/job_catalog/components/JobRoleHeader'
import { NodeHeader } from 'modules/job_catalog/components/NodeHeader'
import {
  NODE_UUID_TYPES,
  findNodeByUuid,
  formatUuidToNodeType,
  getAllSalariesFromChildrenAttributes,
  getChildNodeType,
  getNodeId,
  getSalariesFromAttributes,
  parseTreeNodes,
} from 'modules/job_catalog/lib/nodesUtils'
import { Salary } from 'modules/job_catalog/lib/salary'
import { ItManagementPage } from 'modules/job_catalog/pages/NodePage/ItManagementPage'
import PeoplePage from 'modules/job_catalog/pages/NodePage/PeoplePage'
import SalariesPage from 'modules/job_catalog/pages/NodePage/SalariesPage'

const JobRolesPage = React.lazy(() => import('modules/job_catalog/pages/JobRolesPage'))
const JobCatalogPage = React.lazy(() => import('modules/job_catalog/pages/JobCatalogTreePage'))
const TreeNodePage = React.lazy(() => import('modules/job_catalog/pages/TreeNodePage'))
const UniversalImporterJobCatalogTreePage = React.lazy(
  () => import('modules/job_catalog/pages/UniversalImporterJobCatalogTreePage')
)
const BulkAssignCompetenciesPage = React.lazy(
  () => import('modules/job_catalog/pages/BulkAssingCompetenciesPage')
)
const JobCatalogTreeImportInProgressPage = React.lazy(
  () => import('modules/job_catalog/pages/JobCatalogTreeImportInProgressPage')
)

const NewJobRoleModalPage = React.lazy(
  () => import('modules/job_catalog/pages/NewJobRoleModalPage')
)
const ImportJobsModalPage = React.lazy(
  () => import('modules/job_catalog/pages/ImportJobsModalPage')
)
const ImportJobsErrorPage = React.lazy(
  () => import('modules/job_catalog/pages/ImportJobsErrorPage')
)
const BulkEditPage = React.lazy(() => import('modules/job_catalog/pages/BulkEditPage'))
const Role = React.lazy(() => import('modules/job_catalog/pages/Role'))
const NodePage = React.lazy(() => import('modules/job_catalog/pages/NodePage'))
const NodeRedirectPage = React.lazy(() => import('modules/job_catalog/pages/NodePage/RedirectPage'))
const OverviewPage = React.lazy(() => import('modules/job_catalog/pages/NodePage/OverviewPage'))
const EmployeesPage = React.lazy(() => import('modules/job_catalog/pages/Role/EmployeesPage'))
const SalaryRangesPage = React.lazy(() => import('modules/job_catalog/pages/Role/SalaryRangesPage'))
const CompetenciesPage2 = React.lazy(
  () => import('modules/job_catalog/pages/NodePage/CompetenciesPage')
)
const CareerPathPage = React.lazy(() => import('modules/job_catalog/pages/Role/CareerPathPage'))
const SettingsPage = React.lazy(() => import('modules/job_catalog/pages/Role/SettingsPage'))
const CompetenciesPage = React.lazy(() => import('modules/job_catalog/pages/Role/CompetenciesPage'))
const WorkingConditionsPage = React.lazy(
  () => import('modules/job_catalog/pages/NodePage/WorkingConditionsPage')
)
const AssignCompetenciesModalPage = React.lazy(
  () => import('modules/job_catalog/pages/Role/CompetenciesPage/AssignCompetenciesModalPage')
)
const CreateCompetenciesFromSuggestionsModalPage = React.lazy(
  () =>
    import(
      'modules/job_catalog/pages/Role/CompetenciesPage/CreateCompetenciesFromSuggestionsModalPage'
    )
)
const CreateCompetencyModalPage = React.lazy(
  () => import('modules/job_catalog/pages/Role/CompetenciesPage/CreateCompetencyModalPage')
)
const RootPage = React.lazy(() => import('modules/job_catalog/pages/RootPage'))

const getNavigationForType = (navigation: Navigation<NavigationPaths>, nodeType: string) => {
  const nav = navigation.employees.jobCatalog.root.family
  switch (nodeType.toLowerCase()) {
    case 'family':
      return nav
    case 'function':
      return nav.function
    case 'role':
      return nav.function.role
    case 'level':
      return nav.function.role.nodeLevel
    default:
      return nav
  }
}

const createNodePageRoute = (
  path: string,
  paramName: string,
  nodeType: string,
  navigation: Navigation<NavigationPaths>
): RouteObject => ({
  path,
  element: <NodePage />,
  handle: {
    resource: true,
    meta: ({ watchQuery, params }: MetaContext) =>
      watchQuery({
        query: NodePageDocument,
        variables: {
          nodeId: getNodeId(params[paramName]),
          nodeType: formatUuidToNodeType(params[paramName] || ''),
          childType: getChildNodeType(params[paramName] || ''),
        },
      }).map(({ data }) => {
        if (!data.jobCatalog.treesConnection.nodes[0]) {
          return { title: '' }
        }

        const treeNodes = parseTreeNodes(data.jobCatalog.treesConnection.nodes[0])
        const foundNode = findNodeByUuid(treeNodes, params[paramName] || '')

        if (!foundNode) return { title: '' }

        const isLevel = foundNode?.type === NODE_UUID_TYPES.LEVEL
        let salaries: Salary[]
        if (isLevel) {
          const nodeAttributes = data?.jobCatalog.nodeAttributesConnection.nodes || []
          salaries = getSalariesFromAttributes(nodeAttributes)
        } else {
          salaries = getAllSalariesFromChildrenAttributes(
            data?.jobCatalog.treeNodesConnection.nodes || []
          )
        }

        return {
          title: foundNode.name,
          headerComponent: (
            <NodeHeader
              name={foundNode.name}
              description={foundNode.description || ''}
              type={foundNode.type}
              childrens={foundNode.children}
              salaries={salaries}
              canEditRole
              currentNode={foundNode}
            />
          ),
        }
      }),
  },
  children: [...commonNodeChildrenRoutes(navigation, nodeType)],
})

const commonNodeChildrenRoutes = (
  navigation: Navigation<NavigationPaths>,
  nodeType: string
): RouteObject[] => {
  const nav = getNavigationForType(navigation, nodeType)

  return [
    { index: true, element: <NodeRedirectPage /> },
    {
      path: nav.overview.path,
      element: <OverviewPage />,
    },
    {
      path: nav.workingConditions.path,
      element: <WorkingConditionsPage />,
    },
    {
      path: nav.competencies.path,
      element: <CompetenciesPage2 />,
    },
    {
      path: nav.salaries.path,
      element: <SalariesPage />,
    },
    {
      path: nav.people.path,
      element: <PeoplePage />,
    },
    {
      path: nav.itManagement.path,
      element: <ItManagementPage />,
    },
  ]
}

const JobCatalogIndexRedirect = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const { data } = useQuery(JobCatalogRootPageDocument, {
    variables: { companyId },
  })

  const isLegacyJobCatalogFeatureEnabled = data?.features?.featuresConnection?.nodes?.[0]?.enabled

  if (isLegacyJobCatalogFeatureEnabled) {
    return <Navigate to={navigation.employees.jobCatalog.list.path} replace />
  }

  return <Navigate to={navigation.employees.jobCatalog.overview.path} replace />
}

export const jobCatalogEmployeesRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.employees.jobCatalog.path,
      element: <RootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('job_catalog.title'),
          description: i18n.t('job_catalog.description'),
        }),
      },
      children: [
        {
          index: true,
          element: <JobCatalogIndexRedirect />,
        },
        {
          path: navigation.employees.jobCatalog.list.path,
          element: <JobRolesPage />,
          children: [
            {
              path: navigation.employees.jobCatalog.list.add.path,
              element: <NewJobRoleModalPage />,
            },
            {
              path: navigation.employees.jobCatalog.list.import.path,
              element: <ImportJobsModalPage />,
            },
          ],
        },
        {
          path: navigation.employees.jobCatalog.overview.path,
          element: <JobCatalogPage />,
          children: [
            {
              path: navigation.employees.jobCatalog.overview.node.path,
              element: <TreeNodePage />,
            },
          ],
        },
        {
          path: navigation.employees.jobCatalog.importInProgress.path,
          element: <JobCatalogTreeImportInProgressPage />,
        },
        {
          path: navigation.employees.jobCatalog.importJobCatalogTree.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterJobCatalogTreePage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('job_catalog.import_job_catalog_tree.title'),
              headerComponent: (
                <ResourceHeader
                  title={i18n.t('job_catalog.import_job_catalog_tree.title')}
                  description={i18n.t('job_catalog.import_job_catalog_tree.description')}
                />
              ),
            }),
          },
        },
        {
          path: navigation.employees.jobCatalog.bulkAssignCompetencies.path,
          element: (
            <Routes>
              <Route path="" element={<BulkAssignCompetenciesPage />} />
            </Routes>
          ),
        },
        {
          path: navigation.employees.jobCatalog.import_errors.path,
          element: <ImportJobsErrorPage />,
          handle: {
            meta: () => ({
              title: i18n.t('job_catalog.import_errors_page.title'),
            }),
          },
        },
        {
          path: navigation.employees.jobCatalog.bulkEdit.path,
          element: <BulkEditPage />,
          handle: {
            meta: () => ({
              title: i18n.t('job_catalog.bulk_edit.assign_jobs'),
            }),
          },
        },
        {
          path: navigation.employees.jobCatalog.role.path,
          element: <Role />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: JobRoleHeaderDocument,
                variables: {
                  jobRole: Number(params.id),
                },
              }).map(({ data }) => {
                const jobRole = data.jobCatalog.role

                if (!jobRole) {
                  return { title: '' }
                }
                return {
                  headerComponent: (
                    <JobRoleHeader
                      name={jobRole.name}
                      id={jobRole.id}
                      canEditRole={data.jobCatalog.role?._policies?.editJob.can ?? false}
                    />
                  ),
                  title: jobRole.name,
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <EmployeesPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.salaryRanges.path,
              element: <SalaryRangesPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.settings.path,
              element: <SettingsPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.careerPath.path,
              element: <CareerPathPage />,
            },
            {
              path: navigation.employees.jobCatalog.role.competencies.path,
              element: <CompetenciesPage />,
              children: [
                {
                  path: navigation.employees.jobCatalog.role.competencies.assignCompetencies.path,
                  element: <AssignCompetenciesModalPage />,
                },
                {
                  path: navigation.employees.jobCatalog.role.competencies.createCompetency.path,
                  element: <CreateCompetencyModalPage />,
                },
                {
                  path: navigation.employees.jobCatalog.role.competencies
                    .createCompetenciesFromSuggestions.path,
                  element: <CreateCompetenciesFromSuggestionsModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.employees.jobCatalog.root.path,
          element: <NodePage />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) =>
              watchQuery({
                query: NodePageDocument,
                variables: {
                  nodeId: getNodeId(params.rootId),
                  nodeType: formatUuidToNodeType(params.rootId || ''),
                  childType: getChildNodeType(params.rootId),
                },
              }).map(({ data }) => {
                if (!data.jobCatalog.treesConnection.nodes[0]) {
                  return { title: '' }
                }

                const treeNodes = parseTreeNodes(data.jobCatalog.treesConnection.nodes[0])
                const foundNode = findNodeByUuid(treeNodes, params.rootId || '')

                if (!foundNode) return { title: '' }

                const isLevel = foundNode.type === NODE_UUID_TYPES.LEVEL
                let salaries: Salary[]
                if (isLevel) {
                  const nodeAttributes = data?.jobCatalog.nodeAttributesConnection.nodes || []
                  salaries = getSalariesFromAttributes(nodeAttributes)
                } else {
                  salaries = getAllSalariesFromChildrenAttributes(
                    data?.jobCatalog.treeNodesConnection.nodes || []
                  )
                }

                return {
                  title: foundNode.name,
                  headerComponent: (
                    <NodeHeader
                      name={foundNode.name}
                      description={foundNode.description || ''}
                      type={foundNode.type}
                      childrens={foundNode.children}
                      salaries={salaries}
                      canEditRole
                      currentNode={foundNode}
                    />
                  ),
                }
              }),
          },
          children: [
            ...commonNodeChildrenRoutes(navigation, 'root'),
            {
              ...createNodePageRoute(
                navigation.employees.jobCatalog.root.family.path,
                'familyId',
                'family',
                navigation
              ),
              children: [
                ...commonNodeChildrenRoutes(navigation, 'family'),
                {
                  ...createNodePageRoute(
                    navigation.employees.jobCatalog.root.family.function.path,
                    'functionId',
                    'function',
                    navigation
                  ),
                  children: [
                    ...commonNodeChildrenRoutes(navigation, 'function'),
                    {
                      ...createNodePageRoute('role/:roleId', 'roleId', 'level', navigation),
                      children: [
                        ...commonNodeChildrenRoutes(navigation, 'role'),
                        {
                          ...createNodePageRoute('level/:levelId', 'levelId', 'level', navigation),
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ]
})
